import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getUsersCRMIDsByIds} from 'src/openapi/admin/apis/GetUsersCrmIdsApi';
import {useAppDispatch} from 'src/store/hooks';

import {selectSelectedColumns} from '../slices/user-search-result-columns.slice';
import {
    userSearchResultCrmIdsActions,
    userSearchResultCrmIdsDataSelector,
    userSearchResultCrmIdsResultSelector,
} from '../slices/user-search-result-crm-ids.slice';
import {userSearchResultStateSelector} from '../slices/user-search-result-state.slice';
import {checkShouldLoadColumn} from '../utils';

import type {UserSearchResultCacheKeys} from '../typedef';
import type {UserCrmIds} from 'src/openapi/admin/models/UserCrmIds';


type Props = {
    userIds: number[],
    totalCount: number,
    columnId: string,
};

export const useGetUserSearchResultCrmIds = ({userIds, totalCount, columnId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const selectedColumns = useSelector(selectSelectedColumns);

    const getAction = useCallback(() => apiDispatch(getUsersCRMIDsByIds({
        requestBody: userIds,
    })), [userIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, UserCrmIds>,
        dataCacheKeys: UserSearchResultCacheKeys,
    ) => {
        dispatch(userSearchResultCrmIdsActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    const shouldUpdateData = useMemo(
        () => checkShouldLoadColumn(columnId, selectedColumns),
        [columnId, selectedColumns],
    );

    return useCachedTable({
        dataSelector: userSearchResultCrmIdsDataSelector,
        stateSelector: userSearchResultStateSelector,
        resultSelector: userSearchResultCrmIdsResultSelector,
        actions: userSearchResultCrmIdsActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
        shouldUpdateData,
    });
};
